import React, { useMemo, useState } from "react";
import { Column, Line } from "@ant-design/plots";

const DISCIPLINE_LABELS = {
  free_network: "Free network",
  pending_vetting: "Pending vetting",
  vetted_priority: "Vetted priority",
  priority_applicant: "Priority applicant",
};

function formatShortDate(isoDate) {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatCreatedAt(iso) {
  if (!iso) return " - ";
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso.slice(0, 10);
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function compareValues(a, b, key) {
  const av = a[key] ?? "";
  const bv = b[key] ?? "";
  if (key === "createdAt") return String(av).localeCompare(String(bv));
  return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" });
}

function VelocityCompare({ label, days7, days30, suffix = "" }) {
  return (
    <div className="bg-surgical-navy-700 border border-surgical-navy-600 rounded p-4">
      <div className="font-surgical text-xs text-gray-400 uppercase tracking-wider mb-3">
        {label}
      </div>
      <div className="flex items-stretch gap-3">
        <div className="flex-1">
          <div className="font-surgical text-[10px] text-gray-500 uppercase mb-1">
            7d
          </div>
          <div className="font-surgical text-2xl font-bold text-surgical-teal tabular-nums">
            {days7}
            {suffix}
          </div>
        </div>
        <div className="w-px bg-surgical-navy-600" />
        <div className="flex-1">
          <div className="font-surgical text-[10px] text-gray-500 uppercase mb-1">
            30d
          </div>
          <div className="font-surgical text-2xl font-bold text-white tabular-nums">
            {days30}
            {suffix}
          </div>
        </div>
      </div>
    </div>
  );
}

function FunnelBar({ label, count, max }) {
  const pct = max > 0 ? Math.round((count / max) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between font-surgical text-xs text-gray-400 mb-1">
        <span>{label}</span>
        <span className="text-surgical-teal font-bold tabular-nums">{count}</span>
      </div>
      <div className="h-2 bg-surgical-navy-900 border border-surgical-navy-600">
        <div
          className="h-full bg-surgical-orange transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SortHeader({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  const arrow = active ? (sort.dir === "asc" ? " ↑" : " ↓") : "";
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="font-surgical text-left text-[10px] uppercase tracking-wider text-gray-400 hover:text-surgical-teal w-full"
    >
      {label}
      {arrow}
    </button>
  );
}

const AdminPerformancePanel = ({ metrics, loading }) => {
  const [sort, setSort] = useState({ key: "createdAt", dir: "desc" });

  const signupChartData = useMemo(() => {
    if (!metrics?.trends?.signups7d) return [];
    return metrics.trends.signups7d.map((row) => ({
      date: formatShortDate(row.date),
      count: row.count,
    }));
  }, [metrics]);

  const intakeChartData = useMemo(() => {
    if (!metrics?.trends?.intakes7d) return [];
    return metrics.trends.intakes7d.map((row) => ({
      date: formatShortDate(row.date),
      count: row.count,
    }));
  }, [metrics]);

  const checkoutChartData = useMemo(() => {
    if (!metrics?.trends?.checkouts7d) return [];
    return metrics.trends.checkouts7d.map((row) => ({
      date: formatShortDate(row.date),
      count: row.count,
    }));
  }, [metrics]);

  const sortedVendors = useMemo(() => {
    const rows = [...(metrics?.vendors || [])];
    rows.sort((a, b) => {
      const cmp = compareValues(a, b, sort.key);
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [metrics, sort]);

  const handleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  if (loading && !metrics) {
    return (
      <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-8 text-center">
        <div className="font-surgical text-surgical-teal text-sm uppercase tracking-wider animate-pulse">
          Loading B2C performance metrics…
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const { lifetime, velocity, funnel, meta } = metrics;
  const majorFee = meta?.majorSearchFeeUsd ?? 95;
  const funnel7Max = Math.max(
    funnel.days7.chatsStarted,
    funnel.days7.completedIntakes,
    funnel.days7.checkoutActions,
    1
  );
  const funnel30Max = Math.max(
    funnel.days30.chatsStarted,
    funnel.days30.completedIntakes,
    funnel.days30.checkoutActions,
    1
  );

  return (
    <section className="space-y-6">
      <h2 className="font-surgical text-lg font-bold text-surgical-orange uppercase tracking-wider">
        B2C Performance & Vendor Discipline
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total vendors", value: lifetime.totalRegistered },
          { label: "Free network", value: lifetime.freeNetworkPool },
          {
            label: "Pending vetting",
            value: lifetime.inVettingPipeline,
            accent: "text-surgical-orange",
          },
          {
            label: "Vetted priority",
            value: lifetime.vettedPriority,
            accent: "text-surgical-teal",
            highlight: true,
          },
        ].map((card) => (
          <div
            key={card.label}
            className={`bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4 shadow-surgical ${
              card.highlight ? "border-l-4 border-l-surgical-teal" : ""
            }`}
          >
            <div className="font-surgical text-[10px] text-gray-500 uppercase tracking-wider mb-1">
              {card.label}
            </div>
            <div
              className={`font-surgical text-3xl font-bold tabular-nums ${
                card.accent || "text-white"
              }`}
            >
              {card.value}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <VelocityCompare
          label="New vendor sign-ups"
          days7={velocity.days7.newConversions}
          days30={velocity.days30.newConversions}
        />
        <VelocityCompare
          label="Surgical intake completions"
          days7={velocity.days7.surgicalIntakeCompletions}
          days30={velocity.days30.surgicalIntakeCompletions}
        />
        <VelocityCompare
          label={`$${majorFee} checkout flow`}
          days7={velocity.days7.checkoutValueFlow}
          days30={velocity.days30.checkoutValueFlow}
          suffix={` · ${velocity.days7.checkoutCompleted}/${velocity.days30.checkoutCompleted} paid`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4 min-w-0">
          <div className="font-surgical text-xs text-gray-400 uppercase mb-3">
            Sign-ups (7d)
          </div>
          {signupChartData.length > 0 ? (
            <Column
              data={signupChartData}
              xField="date"
              yField="count"
              height={220}
              color="#FF5722"
              axis={{ x: { labelFill: "#94a3b8" }, y: { labelFill: "#94a3b8" } }}
            />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-500 text-sm">
              No data
            </div>
          )}
        </div>
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4 min-w-0">
          <div className="font-surgical text-xs text-gray-400 uppercase mb-3">
            Intake completions (7d)
          </div>
          {intakeChartData.length > 0 ? (
            <Line
              data={intakeChartData}
              xField="date"
              yField="count"
              height={220}
              color="#14b8a6"
              axis={{ x: { labelFill: "#94a3b8" }, y: { labelFill: "#94a3b8" } }}
            />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-500 text-sm">
              No data
            </div>
          )}
        </div>
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4 min-w-0">
          <div className="font-surgical text-xs text-gray-400 uppercase mb-3">
            ${majorFee} checkouts (7d)
          </div>
          {checkoutChartData.length > 0 ? (
            <Column
              data={checkoutChartData}
              xField="date"
              yField="count"
              height={220}
              color="#00C853"
              axis={{ x: { labelFill: "#94a3b8" }, y: { labelFill: "#94a3b8" } }}
            />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-500 text-sm">
              No data
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4">
          <div className="font-surgical text-xs text-surgical-teal uppercase mb-4 font-bold">
            Intake funnel · 7d
          </div>
          <FunnelBar
            label="Chats started"
            count={funnel.days7.chatsStarted}
            max={funnel7Max}
          />
          <FunnelBar
            label="Completed intakes"
            count={funnel.days7.completedIntakes}
            max={funnel7Max}
          />
          <FunnelBar
            label="Checkout actions"
            count={funnel.days7.checkoutActions}
            max={funnel7Max}
          />
        </div>
        <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4">
          <div className="font-surgical text-xs text-surgical-teal uppercase mb-4 font-bold">
            Intake funnel · 30d
          </div>
          <FunnelBar
            label="Chats started"
            count={funnel.days30.chatsStarted}
            max={funnel30Max}
          />
          <FunnelBar
            label="Completed intakes"
            count={funnel.days30.completedIntakes}
            max={funnel30Max}
          />
          <FunnelBar
            label="Checkout actions"
            count={funnel.days30.checkoutActions}
            max={funnel30Max}
          />
          <p className="font-surgical text-[10px] text-gray-500 mt-3">
            Scanned {meta.aiChatConversationsScanned} ai_chat threads
            {meta.aiChatScanTruncated ? " (cap reached)" : ""}
          </p>
        </div>
      </div>

      <div className="bg-surgical-navy-800 border border-surgical-navy-600 rounded-surgical p-4 overflow-hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="font-surgical text-sm font-bold text-surgical-teal uppercase tracking-wider">
            Vendor discipline roster
          </h3>
          <span className="font-surgical text-xs text-gray-500 tabular-nums">
            {sortedVendors.length} apply-form vendors
          </span>
        </div>
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-[640px] border-collapse font-surgical text-xs">
            <thead>
              <tr className="border-b border-surgical-navy-600">
                <th className="py-2 pr-3">
                  <SortHeader label="Name" sortKey="name" sort={sort} onSort={handleSort} />
                </th>
                <th className="py-2 pr-3">
                  <SortHeader label="Trade" sortKey="trade" sort={sort} onSort={handleSort} />
                </th>
                <th className="py-2 pr-3">
                  <SortHeader label="ZIP" sortKey="zipCode" sort={sort} onSort={handleSort} />
                </th>
                <th className="py-2 pr-3">
                  <SortHeader
                    label="Discipline"
                    sortKey="discipline"
                    sort={sort}
                    onSort={handleSort}
                  />
                </th>
                <th className="py-2 pr-3">
                  <SortHeader
                    label="Program"
                    sortKey="program"
                    sort={sort}
                    onSort={handleSort}
                  />
                </th>
                <th className="py-2">
                  <SortHeader
                    label="Applied"
                    sortKey="createdAt"
                    sort={sort}
                    onSort={handleSort}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedVendors.map((row) => (
                <tr
                  key={row.id}
                  className="border-b border-surgical-navy-700/80 hover:bg-surgical-navy-700/40"
                >
                  <td className="py-2 pr-3 text-white max-w-[140px] truncate">
                    {row.name || " - "}
                  </td>
                  <td className="py-2 pr-3 text-gray-300 max-w-[120px] truncate">
                    {row.trade || " - "}
                  </td>
                  <td className="py-2 pr-3 text-gray-300 tabular-nums">{row.zipCode || " - "}</td>
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-block px-1.5 py-0.5 border text-[10px] uppercase tracking-wide ${
                        row.discipline === "vetted_priority"
                          ? "border-surgical-teal text-surgical-teal"
                          : row.discipline === "pending_vetting"
                            ? "border-surgical-orange text-surgical-orange"
                            : "border-surgical-navy-500 text-gray-400"
                      }`}
                    >
                      {DISCIPLINE_LABELS[row.discipline] || row.discipline}
                    </span>
                  </td>
                  <td className="py-2 pr-3 text-gray-400 capitalize">
                    {row.program === "priority_placement" ? "Priority" : "Network"}
                  </td>
                  <td className="py-2 text-gray-500 tabular-nums whitespace-nowrap">
                    {formatCreatedAt(row.createdAt)}
                  </td>
                </tr>
              ))}
              {sortedVendors.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No vendor applicants yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default AdminPerformancePanel;
